import { useInitLocationSearchMachine } from '../useInitLocationSearchMachine';
import { useLocationSearchFlags } from '../useLocationSearchFlags';
import { useLocationSearchHelpers } from '../useLocationSearchHelpers';

import type { LocationSearchMachineContext } from '../../machine';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Owns the location search machine instance and exposes the actor ref plus its
 * derived `flags` and `helpers`.
 */
export const useLocationSearchMachine = (params: Params) => {
  const { navigateToLocation, navigateToDeliveryLocation, shouldRequestLocationPermission } =
    params;

  const [state, , service] = useInitLocationSearchMachine({
    navigateToLocation,
    navigateToDeliveryLocation,
    shouldRequestLocationPermission,
  });

  const flags = useLocationSearchFlags({ locationSearchActorRef: service });
  const helpers = useLocationSearchHelpers({ locationSearchActorRef: service });

  // ─────────────────────────────────────────────────────────────────────

  return { state, service, flags, helpers };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  /*
    Optional actions that initiates the navigation process for the selected location.
    When those properties are not provided, the standard navigation is used.

    Helpful when a custom navigation method is utilized or when other actions
    need to be performed before navigation.
   */

  navigateToLocation?: (context: LocationSearchMachineContext) => void;
  navigateToDeliveryLocation?: (context: LocationSearchMachineContext) => void;

  /**
   * When `false`, the machine skips the setup phase that would otherwise
   * trigger the OS location permission request. Defaults to `true`.
   */
  shouldRequestLocationPermission?: boolean;
};
